import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { formatWholeUah } from '../../helpers/taxPlanner/money';
import { changeLoss, changeTarget } from '../../store/slices/taxPlannerSlice';
import Card from '../Card';
import TextField from '../TextField';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';

const normalize = (raw) => {
  const value = raw.trim().replace(',', '.');
  return value === '' ? '0' : value;
};

function ParamField({ field, label, placeholder, hint, error, commit, committed }) {
  const [draft, setDraft] = useState(null);
  useEffect(() => setDraft(null), [committed]);
  const shown = draft ?? (field === 'loss' && committed === '0' ? '' : committed);
  const submit = () => {
    if (draft === null) return;
    commit(normalize(draft));
    setDraft(null);
  };
  return (
    <Card data-testid={`param-card-${field}`} className="flex flex-col gap-2.5 px-[18px] py-4">
      <TextField
        label={label}
        labelClassName="mb-1.5 block text-xs font-medium text-text-muted"
        value={shown}
        placeholder={placeholder}
        suffix={jurisdiction.currencySymbol}
        error={error}
        inputMode="decimal"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={submit}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
      />
      <p className="text-xs text-text-muted">{hint}</p>
    </Card>
  );
}

function PlannerParameters() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sub = useAuth().user?.profile?.sub;
  const snapshot = useSelector((state) => state.taxPlanner.snapshot);
  const params = useSelector((state) => state.taxPlanner.params);
  const inputError = useSelector((state) => state.taxPlanner.inputError);
  const persistFailed = useSelector((state) => state.taxPlanner.persistFailed);
  if (!snapshot) return null;

  const year = snapshot.taxYear - 1;
  const errorOf = (field, key) => {
    if (inputError[field]) return t(`taxPlanner.scr02.params.${key}`, { value: formatWholeUah(params[field]) });
    return persistFailed[field] ? t('taxPlanner.scr02.params.notSaved') : undefined;
  };
  const carryForward = t(`taxPlanner.scr02.params.carryForward.${jurisdiction.lossCarryForwardRuleKey}`, { defaultValue: '' });

  return (
    <section data-testid="planner-parameters" className="grid grid-cols-1 gap-3 mid:grid-cols-2">
      <ParamField
        field="loss"
        label={t('taxPlanner.scr02.params.lossLabel', { year })}
        placeholder="0"
        hint={t('taxPlanner.scr02.params.lossHint', { year })}
        error={errorOf('loss', 'lossInvalid')}
        committed={params.loss}
        commit={(value) => dispatch(changeLoss({ sub, value }))}
      />
      <ParamField
        field="target"
        label={t('taxPlanner.scr02.params.targetLabel')}
        hint={t('taxPlanner.scr02.params.targetHint', { carryForward }).replace(/\s{2,}/g, ' ')}
        error={errorOf('target', 'targetInvalid')}
        committed={params.target}
        commit={(value) => dispatch(changeTarget({ sub, value }))}
      />
    </section>
  );
}

export default PlannerParameters;
